import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Checkbox,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  notification,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  SendOutlined,
} from "@ant-design/icons";
import moment from "moment";
import ajaxService from "../../../services/ajax-service";
import { BASE_URL } from "../../../constants";

const { Title, Text } = Typography;
const { TextArea } = Input;

const RSVP_STATUSES = [
  { key: "going", label: "Going" },
  { key: "not_going", label: "Not Going" },
  { key: "undecided", label: "Undecided" },
];

const dispatchBroadcastNotification = async (formData) => {
  const createResponse = await ajaxService.post("/broad-cast-notifications", formData);
  if (!createResponse?.success) {
    return createResponse;
  }

  const broadcast = createResponse.data;
  const broadcastId = broadcast?.id;
  const statusId = Number(broadcast?.status_id);
  const isDraftOrScheduled = statusId === 1 || statusId === 2;

  // Backend sends on create (status 3). Re-calling /send causes 400 "already been sent".
  if (!broadcastId || !isDraftOrScheduled) {
    return {
      success: true,
      message: createResponse.message || "Notification sent successfully",
    };
  }

  const sendResponse = await ajaxService.post(
    `/broadcast-notifications/${broadcastId}/send`
  );

  if (sendResponse?.success) {
    return sendResponse;
  }

  const msg = (sendResponse?.message || "").toLowerCase();
  if (msg.includes("already been sent") || msg.includes("already sent")) {
    return {
      success: true,
      message: createResponse.message || "Notification sent successfully",
    };
  }

  return sendResponse;
};

const EventDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [notifyForm] = Form.useForm();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [notifyModalVisible, setNotifyModalVisible] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [eventAttendees, setEventAttendees] = useState([]);
  const [selectedNotifyUsers, setSelectedNotifyUsers] = useState([]);
  const [selectAllAttendees, setSelectAllAttendees] = useState(false);
  const [activeRsvpTab, setActiveRsvpTab] = useState("going");
  const [rsvpData, setRsvpData] = useState({
    data: [],
    meta: { current_page: 1, per_page: 15, total: 0 },
  });
  const [rsvpLoading, setRsvpLoading] = useState(false);

  const fetchEvent = useCallback(async () => {
    setLoading(true);
    const response = await ajaxService.get(`/admin/events/${id}`);
    if (response?.success) {
      setEvent(response.data);
    } else {
      notification.open({
        message: response?.message || "Failed to load event",
        icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
    }
    setLoading(false);
  }, [id]);

  const fetchRsvps = useCallback(
    async (status, page = 1) => {
      setRsvpLoading(true);
      const response = await ajaxService.get(
        `/admin/events/${id}/rsvps?status=${status}&page=${page}&limit=15`
      );
      if (response?.success) {
        const data = response.data;
        setRsvpData({
          data: data.data || [],
          meta: data.meta || { current_page: page, per_page: 15, total: 0 },
        });
      }
      setRsvpLoading(false);
    },
    [id]
  );

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  useEffect(() => {
    if (id) {
      fetchRsvps(activeRsvpTab, 1);
    }
  }, [id, activeRsvpTab, fetchRsvps]);

  useEffect(() => {
    if (location.state?.openNotify && event && !loading) {
      openNotifyModal(location.state.preselectedUserIds || []);
      history.replace(`/events/${id}`);
    }
  }, [location.state, event, loading, id, history]);

  const handleDeleteImage = async (imageId) => {
    const response = await ajaxService.delete("/event-del-image/", imageId);
    if (response?.success) {
      notification.open({
        message: "Image deleted successfully",
        icon: <CheckCircleOutlined style={{ color: "var(--success-green)" }} />,
      });
      fetchEvent();
    } else {
      notification.open({
        message: response?.message || "Failed to delete image",
        icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
    }
  };

  const fetchGoingAttendees = useCallback(async () => {
    setAttendeesLoading(true);
    const response = await ajaxService.get(
      `/admin/events/${id}/rsvps?status=going&page=1&limit=500`
    );

    const attendees = response?.success
      ? (response.data?.data || [])
          .map((rsvp) => {
            const user = rsvp.user;
            if (!user?.id) return null;
            return {
              id: user.id,
              name: user.name || user.email,
              email: user.email,
              rsvpStatus: rsvp.status,
            };
          })
          .filter(Boolean)
      : [];

    setEventAttendees(attendees);
    setAttendeesLoading(false);
    return attendees;
  }, [id]);

  const openNotifyModal = async (preselectedUserIds = []) => {
    const attendees = await fetchGoingAttendees();
    if (attendees.length === 0) {
      notification.open({
        message: "No going users found for this event",
        icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
      return;
    }

    const goingIds = new Set(attendees.map((a) => String(a.id)));
    const ids = preselectedUserIds.map(String).filter((userId) => goingIds.has(userId));
    setSelectAllAttendees(ids.length === 0);
    setSelectedNotifyUsers(ids.length > 0 ? ids : attendees.map((a) => String(a.id)));
    notifyForm.setFieldsValue({
      title: event?.name ? `${event.name}` : "Event Notification",
      message: "",
    });
    setNotifyModalVisible(true);
  };

  const handleSelectAllAttendees = (checked) => {
    setSelectAllAttendees(checked);
    if (checked) {
      setSelectedNotifyUsers(eventAttendees.map((a) => String(a.id)));
    } else {
      setSelectedNotifyUsers([]);
    }
  };

  const handleNotifyUsersChange = (values) => {
    setSelectedNotifyUsers(values);
    setSelectAllAttendees(
      eventAttendees.length > 0 && values.length === eventAttendees.length
    );
  };

  const handleSendNotification = async (values) => {
    if (!selectedNotifyUsers.length) {
      notification.open({
        message: "Select at least one going user",
        icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
      return;
    }

    setNotifyLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("message", values.message);
      selectedNotifyUsers.forEach((userId) => {
        formData.append("recipients[]", userId);
      });

      const response = await dispatchBroadcastNotification(formData);
      if (!response?.success) {
        notification.open({
          message: response?.message || "Failed to send notification",
          icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
        });
        return;
      }

      notification.open({
        message:
          response.message ||
          `Notification sent to ${selectedNotifyUsers.length} going user(s)`,
        icon: <CheckCircleOutlined style={{ color: "var(--success-green)" }} />,
      });
      setNotifyModalVisible(false);
      notifyForm.resetFields();
      setSelectedNotifyUsers([]);
      setSelectAllAttendees(false);
    } catch (error) {
      console.error("Send notification error:", error);
      notification.open({
        message: "Failed to send notification",
        icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    const response = await ajaxService.delete("/admin/events/", id);
    if (response?.success) {
      notification.open({
        message: "Event deleted successfully",
        icon: <CheckCircleOutlined style={{ color: "var(--success-green)" }} />,
      });
      history.push("/events");
    } else {
      notification.open({
        message: response?.message || "Failed to delete event",
        icon: <CloseCircleOutlined style={{ color: "var(--error-red)" }} />,
      });
    }
    setDeleteModalVisible(false);
  };

  const rsvpColumns = [
    {
      title: "Name",
      key: "name",
      width: 180,
      render: (_, record) => (
        <Text style={{ fontSize: "0.85rem" }}>{record.user?.name || "N/A"}</Text>
      ),
    },
    {
      title: "Email",
      key: "email",
      width: 220,
      ellipsis: true,
      render: (_, record) => (
        <Text style={{ fontSize: "0.85rem" }}>{record.user?.email || "N/A"}</Text>
      ),
    },
    {
      title: "Phone",
      key: "phone",
      width: 140,
      render: (_, record) => (
        <Text style={{ fontSize: "0.85rem" }}>{record.user?.phone || "N/A"}</Text>
      ),
    },
    {
      title: "RSVP Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (createdAt) => (
        <Text style={{ fontSize: "0.85rem" }}>
          {createdAt ? moment(createdAt).format("DD-MMM-YYYY h:mm A") : "N/A"}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      fixed: "right",
      render: (_, record) =>
        activeRsvpTab === "going" && record.user?.id ? (
          <Button
            type="text"
            size="small"
            icon={<SendOutlined />}
            onClick={() => openNotifyModal([record.user.id])}
            title="Send notification"
          />
        ) : null,
    },
  ];

  const spotsRemainingText =
    event?.max_capacity != null
      ? `${event.spots_remaining ?? 0} of ${event.max_capacity} spots remaining`
      : null;

  if (loading) {
    return (
      <div className="page-container" style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="page-container">
        <Empty description="Event not found" />
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => history.push("/events")}>
            Back to Events
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="module-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
        <div>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => history.push("/events")}
            style={{ color: "var(--text-gray)", padding: 0, marginBottom: 8 }}
          >
            Back to Events
          </Button>
          <h1 className="module-title">{event.name}</h1>
          <p className="module-subtitle">Event details and attendee RSVPs</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => openNotifyModal()}
          >
            Send Notification
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={() => history.push("/events", { editEventId: Number(id) })}
          >
            Edit
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={() => setDeleteModalVisible(true)}>
            Delete
          </Button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <div className="stats-card">
          <div className="stats-value">{event.going_count ?? 0}</div>
          <div className="stats-label">Going</div>
        </div>
        <div className="stats-card">
          <div className="stats-value">{event.capacity_display || "Unlimited"}</div>
          <div className="stats-label">Maximum Capacity</div>
        </div>
        {spotsRemainingText && (
          <div className="stats-card">
            <div className="stats-value">{event.spots_remaining_display}</div>
            <div className="stats-label">Spots Remaining</div>
          </div>
        )}
        <div className="stats-card">
          <div className="stats-value">
            <Tag color={event.is_full ? "red" : "green"}>{event.status || "Open"}</Tag>
          </div>
          <div className="stats-label">Status</div>
        </div>
      </div>

      <Card className="data-table-card" style={{ marginBottom: 24 }}>
        <Descriptions
          bordered
          column={{ xs: 1, sm: 2 }}
          size="middle"
          labelStyle={{ color: "var(--text-white)", fontWeight: 600, background: "var(--dark-bg-hover)" }}
          contentStyle={{ color: "var(--text-gray)" }}
        >
          <Descriptions.Item label="Date">
            {event.date ? moment(event.date).format("DD-MMM-YYYY") : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Time">
            {event.start_time || event.end_time
              ? `${event.start_time || "—"} – ${event.end_time || "—"}`
              : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Address" span={2}>
            {event.address || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Going">{event.going_display || event.going_count}</Descriptions.Item>
          <Descriptions.Item label="Code">{event.code || "N/A"}</Descriptions.Item>
          {event.link && (
            <Descriptions.Item label="Link" span={2}>
              <a href={event.link} target="_blank" rel="noopener noreferrer">
                {event.link}
              </a>
            </Descriptions.Item>
          )}
          {event.description && (
            <Descriptions.Item label="Description" span={2}>
              {event.description}
            </Descriptions.Item>
          )}
          {spotsRemainingText && (
            <Descriptions.Item label="Spots remaining" span={2}>
              {spotsRemainingText}
            </Descriptions.Item>
          )}
        </Descriptions>

        {event.images?.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Title level={5} style={{ color: "var(--text-white)", marginBottom: 12 }}>
              Images
            </Title>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {event.images.map((img) => (
                <div key={img.id} style={{ position: "relative" }}>
                  <img
                    src={`${BASE_URL}/storage/${img.image}`}
                    alt="Event"
                    style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 8 }}
                  />
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.5)" }}
                    onClick={() => handleDeleteImage(img.id)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card className="data-table-card" title={<span style={{ color: "var(--text-white)" }}>Attendees</span>}>
        <Tabs
          activeKey={activeRsvpTab}
          onChange={(key) => setActiveRsvpTab(key)}
          items={RSVP_STATUSES.map(({ key, label }) => ({
            key,
            label,
            children: (
              <Table
                columns={rsvpColumns}
                dataSource={rsvpData.data}
                rowKey="id"
                loading={rsvpLoading}
                pagination={{
                  current: rsvpData.meta?.current_page || 1,
                  pageSize: rsvpData.meta?.per_page || 15,
                  total: rsvpData.meta?.total || 0,
                  showSizeChanger: false,
                  onChange: (page) => fetchRsvps(activeRsvpTab, page),
                }}
                size="small"
              />
            ),
          }))}
        />
      </Card>

      <Modal
        open={notifyModalVisible}
        onCancel={() => {
          setNotifyModalVisible(false);
          notifyForm.resetFields();
          setSelectedNotifyUsers([]);
          setSelectAllAttendees(false);
        }}
        footer={null}
        centered
        className="modal-overlay"
        width={560}
        maskClosable={false}
      >
        <div
          style={{
            padding: "var(--spacing-2xl) var(--spacing-xl)",
            background: "var(--dark-bg-card)",
            borderRadius: "var(--radius-lg)",
          }}
        >
          <Title level={4} style={{ color: "var(--text-white)", textAlign: "center", marginBottom: 24 }}>
            Send Event Notification
          </Title>
          <Text style={{ color: "var(--text-gray)", display: "block", marginBottom: 16 }}>
            Notify users who marked Going for this event only.
          </Text>

          <Form form={notifyForm} layout="vertical" onFinish={handleSendNotification}>
            <Form.Item
              label={<span style={{ color: "var(--text-white)", fontWeight: 600 }}>Title</span>}
              name="title"
              rules={[{ required: true, message: "Title is required" }]}
            >
              <Input placeholder="Notification title" className="input-field" />
            </Form.Item>

            <Form.Item
              label={<span style={{ color: "var(--text-white)", fontWeight: 600 }}>Message</span>}
              name="message"
              rules={[{ required: true, message: "Message is required" }]}
            >
              <TextArea rows={4} placeholder="Enter your message" className="input-field" />
            </Form.Item>

            <Form.Item
              label={<span style={{ color: "var(--text-white)", fontWeight: 600 }}>Recipients</span>}
              required
            >
              <Checkbox
                checked={selectAllAttendees}
                onChange={(e) => handleSelectAllAttendees(e.target.checked)}
                style={{ color: "var(--text-white)", marginBottom: 8 }}
              >
                All going users ({eventAttendees.length})
              </Checkbox>
              <Select
                mode="multiple"
                showSearch
                placeholder="Select going users"
                value={selectedNotifyUsers}
                onChange={handleNotifyUsersChange}
                loading={attendeesLoading}
                className="input-field"
                style={{ width: "100%" }}
                optionFilterProp="label"
                options={eventAttendees.map((attendee) => ({
                  value: String(attendee.id),
                  label: attendee.name,
                }))}
              />
            </Form.Item>

            <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 24 }}>
              <Button
                onClick={() => {
                  setNotifyModalVisible(false);
                  notifyForm.resetFields();
                  setSelectedNotifyUsers([]);
                  setSelectAllAttendees(false);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={notifyLoading} icon={<SendOutlined />}>
                Send
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      <Modal
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={null}
        centered
        className="modal-overlay"
      >
        <div style={{ padding: 24, textAlign: "center" }}>
          <DeleteOutlined style={{ fontSize: 48, color: "var(--error-red)", marginBottom: 16 }} />
          <Title level={4} style={{ color: "var(--text-white)" }}>
            Delete Event?
          </Title>
          <Text style={{ color: "var(--text-gray)" }}>
            This will permanently delete the event, its images, and RSVP records.
          </Text>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 24 }}>
            <Button onClick={() => setDeleteModalVisible(false)}>Cancel</Button>
            <Button danger type="primary" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EventDetails;
